import { css, SerializedStyles } from '@emotion/core'
import { useLocation } from '@reach/router'
import { GatsbyImage } from 'gatsby-plugin-image'
import parse from 'html-react-parser'
import React from 'react'

import { breakpoints, emptySpaceWidthBreakPoint } from '../constants/breakPoints'
import { headerHeight } from '../constants/header'
import { isMobile } from '../helpers/common'
import { useDetectSticky } from '../hooks/useStickyObserver'
import Block from './Block'
import Image from './Image'
import { Link } from './Link'
import SearchBar, { searchBarHeight } from './Search'
import VerticalTextDivider from './VerticalTextDivider'

export const stickyCardsNavigationHeight = 64 //px

interface CardsNavigationProps {
  id?: string
  blackBG?: boolean
  heading?: string
  categories: {
    node: {
      slug?: string
      // only for product-learn (subcategories)
      subSlug?: string
      name: string
      navigationIcon: {
        title?: string
        // if data is queried from contentful
        gatsbyImageData?: any
        // if data is imported from local constant
        localSrc?: string
      }
      categoryColor?: string
      soon?: boolean
    }
  }[]
  columns?: number
  isCentered?: boolean
  deselectable?: boolean
  currentIndex?: number
  setCurrentIndex?: React.Dispatch<React.SetStateAction<number>>
  isHorisontal?: boolean
  hrefPrefix?: string
  breakPoint?: number
  stickyLayoutOnBreakPoint?: boolean
  hideUntilSticky?: boolean
  referenceIdForStickiness?: string
  referenceIdForScrollOnClick?: string
  withHorizontalScroll?: boolean
  withSearch?: boolean
  keepSearchOnSmallScreens?: boolean
  search?: string
  setSearch?: (value: string) => void
  withBorderBottom?: boolean
  cssStyles?: SerializedStyles
}

export const CardsNavigation: React.FC<CardsNavigationProps> = ({
  id,
  heading,
  blackBG = false,
  categories,
  columns = categories.length,
  isCentered,
  deselectable,
  currentIndex,
  setCurrentIndex,
  isHorisontal = false,
  hrefPrefix = '',
  breakPoint = breakpoints.small,
  stickyLayoutOnBreakPoint = false,
  hideUntilSticky = false,
  referenceIdForStickiness,
  referenceIdForScrollOnClick,
  withHorizontalScroll = false,
  withSearch = false,
  keepSearchOnSmallScreens = false,
  search = '',
  setSearch = () => {},
  withBorderBottom = false,
  cssStyles,
}) => {
  const location = useLocation()

  if (stickyLayoutOnBreakPoint && isMobile(breakPoint)) {
    // to add bottom-border & background styles when container becomes sticky
    // referenceIdForStickiness is the id of the element above the sticky element
    // or the sticky container itself (by default)
    const mobileContainerIsSticky = useDetectSticky(referenceIdForStickiness || 'sticky-sub-nav', headerHeight + 1)

    const [centerFlexContainerOnStickyLayout, setCenterFlexContainerOnStickyLayout] = React.useState(false)

    React.useEffect(() => {
      const selectedElement = document.getElementsByClassName('selected')[0]
      const scrollContainer = document.getElementById('sub-nav-scroll-container')
      const childrenWidthsArray = scrollContainer
        ? Array.from(scrollContainer?.children).map((child: any) => child.offsetWidth)
        : []

      setCenterFlexContainerOnStickyLayout(
        // check if all children fit in the screen
        // width of children + gap between them + padding on the sides <= window width
        childrenWidthsArray.reduce((total, width) => total + width, 0) + (childrenWidthsArray.length - 1) * 16 + 48 <=
          window.innerWidth
      )

      if (!centerFlexContainerOnStickyLayout && selectedElement && scrollContainer) {
        const offset = selectedElement.getBoundingClientRect().left
        scrollContainer.scrollTo({
          // scroll element to the center of the screen or to the Divider (if keepSearchOnSmallScreens is true)
          left:
            offset -
            (isMobile(breakpoints.small) && withSearch && keepSearchOnSmallScreens
              ? 97
              : window.innerWidth / 2 - selectedElement.clientWidth / 2),
          behavior: 'smooth',
        })
      }
    }, [])

    const scrollHandlerStickyLayout = () => {
      if (referenceIdForScrollOnClick) {
        window.scrollTo({
          top:
            window.scrollY -
            headerHeight -
            stickyCardsNavigationHeight -
            // margin-top: 2rem
            2 * 16 +
            (document.getElementById(referenceIdForScrollOnClick)?.getBoundingClientRect().top ?? 0),
          behavior: 'smooth',
        })
      }
    }

    return (
      <div className="full-screen-sticky" css={[stickyWrapperContainerStyle(hideUntilSticky, breakPoint), cssStyles]}>
        <div
          id="sticky-sub-nav"
          className="full-screen-sticky"
          css={stickyMobileContainerStyle(
            blackBG,
            mobileContainerIsSticky,
            withBorderBottom,
            withSearch,
            keepSearchOnSmallScreens,
            hideUntilSticky,
            referenceIdForStickiness
          )}
        >
          {withSearch && (keepSearchOnSmallScreens || !isMobile(breakpoints.small)) && (
            <div css={searchBarContainerStyle}>
              <SearchBar useLoupeIconOnly search={search} setSearch={setSearch} cssStyles={searchBarStyle(blackBG)} />
              <VerticalTextDivider
                color={blackBG ? '#808080' : '#efeff0'}
                height="40px"
                translateY="0"
                marginLeftRight="1rem"
              />
            </div>
          )}
          <div
            id="sub-nav-scroll-container"
            className="full-screen-relative"
            css={stickyMobileContentStyle(withSearch, centerFlexContainerOnStickyLayout, keepSearchOnSmallScreens)}
          >
            {categories.map(({ node: { slug, subSlug, name, categoryColor, soon } }, index) => (
              <Link
                key={`strip-navigation-${slug ? slug : name}`}
                className={`strong-body-1 ${
                  ((slug && location.pathname === (!!hrefPrefix ? `/${hrefPrefix}/${slug}/` : `/${slug}/`)) ||
                    (subSlug && location.pathname === `/${subSlug}/`) ||
                    index === currentIndex) &&
                  'selected'
                }`}
                onClick={() => (
                  !soon &&
                    setCurrentIndex &&
                    (deselectable ? setCurrentIndex(currentIndex === index ? -1 : index) : setCurrentIndex(index)),
                  referenceIdForScrollOnClick && scrollHandlerStickyLayout()
                )}
                href={!soon && slug ? (!!hrefPrefix ? `/${hrefPrefix}/${slug}/` : `/${slug}/`) : undefined}
                css={stickyMobileItemStyle(
                  (slug && location.pathname === (!!hrefPrefix ? `/${hrefPrefix}/${slug}/` : `/${slug}/`)) ||
                    (subSlug && location.pathname === `/${subSlug}/`) ||
                    index === currentIndex,
                  blackBG,
                  soon,
                  categoryColor
                )}
              >
                {name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (withHorizontalScroll) {
    return (
      <div id={id} css={[horizontalScrollContainerStyle, cssStyles]}>
        <h2 className="heading2">{heading}</h2>
        <div className="full-screen-relative" css={horizontalScrollTrackContainerStyle}>
          <div className="full-screen-relative" css={horizontalScrollTrackStyle}>
            {categories.map(({ node: { slug, name, navigationIcon, categoryColor, soon } }, index) => (
              <Link
                key={`cards-navigation-${slug ? slug : name}`}
                onClick={() =>
                  !soon && setCurrentIndex && setCurrentIndex(currentIndex === index && deselectable ? -1 : index)
                }
                href={!soon && slug ? (!!hrefPrefix ? `/${hrefPrefix}/${slug}/` : `/${slug}/`) : undefined}
                css={categoryCardStyle(
                  (slug && location.pathname === (!!hrefPrefix ? `/${hrefPrefix}/${slug}/` : `/${slug}/`)) ||
                    index === currentIndex,
                  withHorizontalScroll,
                  soon,
                  categoryColor
                )}
              >
                {navigationIcon.gatsbyImageData && (
                  <GatsbyImage
                    image={navigationIcon.gatsbyImageData}
                    alt={navigationIcon.title || name}
                    imgStyle={{ maxHeight: '53px', width: 'auto' }}
                    style={{ maxHeight: '53px', width: 'auto' }}
                  />
                )}

                {navigationIcon.localSrc && (
                  <Image
                    src={navigationIcon.localSrc}
                    alt={name}
                    imgStyle={{ maxHeight: '53px', width: 'auto' }}
                    css={imageStyle}
                  />
                )}

                <p className={`subtitle bold ${soon ? 'gray' : 'black'}-text`}>
                  {parse(name)} {soon && <span className="soon">Soon</span>}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const scrollHandlerDesktopLayout = (e: React.MouseEvent<HTMLElement>) => {
    window.scrollTo({
      top:
        window.scrollY -
        headerHeight -
        (document.getElementById('search-bar') ? searchBarHeight : 0) -
        // margin-top: 2rem
        2 * 16 +
        (e.currentTarget.getBoundingClientRect().top ?? 0),
      behavior: 'smooth',
    })
  }
  return (
    <div id={id}>
      {heading && isCentered && (
        <h2 className="heading2 center" css={heading2Style(isCentered)}>
          {heading}
        </h2>
      )}
      <Block
        columns={isHorisontal ? 2 : 1}
        alignContent="flex-start"
        columnsTemplate={isHorisontal ? '1fr 2fr' : '1fr'}
        rowGap={0}
        columnGap={0.5}
        css={[containerStyle(isHorisontal, isCentered), !!cssStyles && cssStyles]}
      >
        {heading && !isCentered && (
          <h2 className="heading2" css={heading2Style(isCentered)}>
            {heading}
          </h2>
        )}
        <Block
          onClick={(e) => setCurrentIndex && scrollHandlerDesktopLayout(e)}
          columns={columns}
          rowGap={1}
          noBreakForTablet
          twoColumnsForMobile
          css={blockStyle(columns, isHorisontal, isCentered)}
        >
          {categories.map(({ node: { slug, name, navigationIcon, categoryColor, soon } }, index) => (
            <Link
              key={`cards-navigation-${slug ? slug : name}`}
              onClick={() =>
                !soon && setCurrentIndex && setCurrentIndex(currentIndex === index && deselectable ? -1 : index)
              }
              href={!soon && slug ? (!!hrefPrefix ? `/${hrefPrefix}/${slug}/` : `/${slug}/`) : undefined}
              css={categoryCardStyle(
                (slug && location.pathname === (!!hrefPrefix ? `/${hrefPrefix}/${slug}/` : `/${slug}/`)) ||
                  index === currentIndex,
                withHorizontalScroll,
                soon,
                categoryColor
              )}
            >
              {navigationIcon.gatsbyImageData && (
                <GatsbyImage
                  image={navigationIcon.gatsbyImageData}
                  alt={navigationIcon.title || name}
                  imgStyle={{ maxHeight: '53px', width: 'auto' }}
                  style={{ maxHeight: '53px', width: 'auto' }}
                />
              )}

              {navigationIcon.localSrc && (
                <Image
                  src={navigationIcon.localSrc}
                  alt={name}
                  imgStyle={{ maxHeight: '53px', width: 'auto' }}
                  css={imageStyle}
                />
              )}

              <p className={`subtitle bold ${soon ? 'gray' : 'black'}-text`}>
                {parse(name)} {soon && <span className="soon">Soon</span>}
              </p>
            </Link>
          ))}
        </Block>
      </Block>
    </div>
  )
}

// PLS BE CAREFUL WHEN CHANGING MIN- / MAX-WIDTH OF SINGLE CARDS
// CAREFULLY CHECK ALL RELEVANT LOGIC IN CSS BELOW

const blockStyle = (columns: number, isHorisontal: boolean, isCentered?: boolean) => css`
  ${!isHorisontal &&
  !isCentered &&
  `@media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: 1100px) {
    grid-template-columns: repeat(${columns / 2}, 1fr);
  }`};

  ${isCentered && // amount of columns * (min-width of column + column-gap) - 1 x column-gap
  `
    max-width: calc(${columns} * 11rem - 1rem);
    
    // LOGIC FOR MAX-WIDTH < REQ WIDTH TO DISPLAY ALL CARDS IN 1 LINE:
    @media screen and (max-width: calc(${columns} * 11rem - 1rem)) {  
      // MORE THAN 3 COLUMNS: BREAK CARDS INTO TWO LINES: 
        // IF AMOUNT OF COLUMNS = EVEN: TWO LINES WITH EQUAL AMOUNT OF CARDS
        // IF AMOUNT OF COLUMNS = ODD:  1ST LINE DISPLAYS 1 MORE CARD THAN 2ND
      ${columns > 3 && `max-width: calc(((${columns} * 11rem - 1rem) / 2) + ${columns % 2 ? '10rem' : '0rem'})`};
      display: flex;
      flex-wrap: wrap;
      justify-content: center;

      a {
        max-width: 10rem;
      }
    }

    // BACK TO GRID 1fr 1fr FOR SMALL SCREENS:
    @media screen and (max-width: ${breakpoints.small}px) {
      display: grid;
      max-width: 100%;

      a {
        max-width: initial;
      }
    }
  `}
`

const categoryCardStyle = (
  isSelected: boolean,
  withHorizontalScroll: boolean,
  soon?: boolean,
  categoryColor?: string
) => css`
  padding: 1rem;
  border: 1px solid ${isSelected ? '#000' : '#efeff0'};
  border-radius: 0.5rem;
  height: 100%;
  min-height: 9rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  min-width: 10rem;
  transition: all 0.2s ease-in-out;

  .subtitle {
    margin-top: auto;
  }

  &:hover {
    ${!soon &&
    `
      ${!isSelected && `border: 1px solid ${categoryColor ? categoryColor : '#318eff'};`};
      box-shadow: rgba(0, 0, 0, 0.1) 0px 10px 20px 0px;
    `}
  }

  ${soon &&
  `
    pointer-events: none;
    img { 
      filter: grayscale(100%) 
    }
    .soon {
      padding: 0 5px;
      transform: translateY(-2px);
    }
  `}

  ${!withHorizontalScroll &&
  `
    @media screen and (max-width: ${breakpoints.medium}px) {
      // to allow 4 columns on smallest medium size (641px)
      min-width: 136px;
    }
  `}
`

const heading2Style = (isCentered?: boolean) => css`
  margin: 0 0.5rem 2.5rem 0 !important;
  ${isCentered && '&.heading2 { text-align: center !important }'};
`

const containerStyle = (isHorisontal: boolean, isCentered?: boolean) => css`
  @media screen and (max-width: 1100px) {
    grid-template-columns: ${isHorisontal ? '1fr' : '1fr 2fr'};

    ${isCentered &&
    `
      display: flex;
      justify-content: center;
    `};
  }

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const imageStyle = css`
  &.gatsby-image-wrapper {
    max-height: 53px;
    width: auto;
  }
`

const stickyWrapperContainerStyle = (hideUntilSticky: boolean, breakPoint: number) => css`
  z-index: 2;
  overflow: ${hideUntilSticky ? 'visible' : 'hidden'};
  ${hideUntilSticky && 'max-height: 0'};

  @media screen and (max-width: ${breakPoint}px) {
    position: sticky;
    top: ${headerHeight - 1}px;
  }
`

const stickyMobileContainerStyle = (
  blackBG: boolean,
  mobileContainerIsSticky: boolean,
  withBorderBottom: boolean,
  withSearch: boolean,
  keepSearchOnSmallScreens: boolean,
  hideUntilSticky: boolean,
  referenceIdForStickiness?: string
) => css`
  position: sticky;
  top: ${headerHeight - 1}px;
  background-color: ${blackBG ? 'rgba(7, 19, 34, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
  backdrop-filter: blur(20px);
  overflow: hidden;
  z-index: 2;

  &::before,
  &::after {
    position: absolute;
    content: '';
    background: linear-gradient(
      90deg,
      ${blackBG
        ? 'rgba(7, 19, 34, 1) 85%, rgba(7, 19, 34, 0) 100%'
        : 'rgba(255, 255, 255, 1) 85%, rgba(255, 255, 255, 0) 100%'}
    );
    height: 100%;
    width: calc((100vw - 1136px) / 2);
    z-index: 1;
    top: 0;

    @media screen and (max-width: ${breakpoints.xlarge}px) {
      background: linear-gradient(
        90deg,
        ${blackBG ? 'rgba(7, 19, 34, 1), rgba(7, 19, 34, 0)' : 'rgba(255, 255, 255, 1), rgba(255, 255, 255, 0)'}
      );
    }

    @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
      width: 2rem;
    }

    @media screen and (max-width: ${breakpoints.medium}px) {
      width: 1.5rem;
    }
  }

  &::before {
    left: -1px;
  }

  &::after {
    right: -1px;
    transform: rotateZ(180deg);
  }

  ${withSearch &&
  `
    display: grid;
    grid-template-columns: auto auto;
    align-items: stretch;

    &::before {
      display: none;

      @media screen and (max-width: ${breakpoints.small}px) {
        ${!keepSearchOnSmallScreens && 'display: block'};
      }
    }
  `};

  ${(mobileContainerIsSticky || withBorderBottom) &&
  `border-bottom: 1px solid ${blackBG ? 'rgba(255, 255, 255, 0.2)' : '#efeff0'};`};

  ${hideUntilSticky &&
  // if referenceIdForStickiness is not defined, the sticky container itself is the reference
  // in that case, we can't transform as the translation would hinder the stickiness itself
  `
    ${!mobileContainerIsSticky && 'pointer-events: none; z-index: -1;'};
    transform: translateY(${mobileContainerIsSticky || !referenceIdForStickiness ? '0' : '-100%'});
    opacity: ${mobileContainerIsSticky ? '1' : '0'};
    transition: all 0.3s ease-in-out;
  `}
`

const searchBarContainerStyle = css`
  z-index: 2;
  display: flex;
  align-items: center;
  background: transparent;
`

const searchBarStyle = (blackBG: boolean) => css`
  border: 1px solid ${blackBG ? '#808080' : '#efeff0'};
  border-radius: 0.5rem;
  box-sizing: border-box;
  background: ${blackBG ? '#28323E' : '#fafbfc'};
  height: 40px;
  width: 40px;

  svg {
    margin: auto;
    ${blackBG && 'path { stroke: #efeff0; }'};
  }

  &:hover {
    border: 1px solid #318eff;
  }
`

const stickyMobileContentStyle = (
  withSearch: boolean,
  centerFlexContainerOnStickyLayout: boolean,
  keepSearchOnSmallScreens?: boolean
) => css`
  position: relative;
  display: flex;
  align-items: center;
  overflow: scroll;
  column-gap: calc(1rem + 2px);

  ${!withSearch && centerFlexContainerOnStickyLayout && 'justify-content: center'};

  &::-webkit-scrollbar {
    display: none;
  }
  /* for firefox */
  scrollbar-width: none;

  ${withSearch &&
  `
    &.full-screen-relative {
      padding-left: calc(1rem + 2px) !important;
      margin-left: calc(var(--emptySpaceWidth) - 1rem);
      
      @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
        padding-left: 1rem !important;
        margin-left: 1rem;
      }
  
      @media screen and (max-width: ${breakpoints.medium}px) {
        padding-left: 1rem !important;
        margin-left: 0.5rem;
      }
      
      @media screen and (max-width: ${breakpoints.small}px) {
        ${!keepSearchOnSmallScreens && 'padding-left: 1.5rem !important'};
        ${!keepSearchOnSmallScreens && 'margin-left: 0'};
      }
    }
  `}
`

const stickyMobileItemStyle = (isSelected: boolean, blackBG: boolean, soon?: boolean, categoryColor?: string) => css`
  white-space: nowrap;
  box-sizing: border-box;
  padding: calc(0.5rem - 1px) 1rem;
  margin: 0.75rem 0;
  border: 1px solid ${isSelected && categoryColor ? categoryColor : blackBG ? '#808080' : '#efeff0'};
  border-radius: 0.5rem;
  color: ${isSelected ? '#fff' : categoryColor ? categoryColor : blackBG ? '#fff' : '#000'};
  background-color: ${isSelected ? (categoryColor ? categoryColor : '#000') : blackBG ? '#28323E' : '#fafafa'};

  &:hover {
    ${!soon &&
    `
      ${!isSelected && `border: 1px solid ${categoryColor ? categoryColor : '#318eff'};`};
    `}
  }
`

const horizontalScrollContainerStyle = css`
  margin: 4rem 0;
`

const horizontalScrollTrackContainerStyle = css`
  position: relative;
  height: 9rem;
  margin: 4rem 0;
  overflow: hidden;

  &::before,
  &::after {
    position: absolute;
    content: '';
    background: linear-gradient(to right, rgba(255, 255, 255, 1) 90%, rgba(255, 255, 255, 0));
    height: 100%;
    width: calc((100vw - 1136px) / 2);
    z-index: 1;
    top: 0;

    @media screen and (max-width: ${breakpoints.xxxlarge}px) {
      background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
    }

    @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
      width: 2rem;
    }

    @media screen and (max-width: ${breakpoints.medium}px) {
      width: 1.5rem;
    }
  }

  &::before {
    left: -1px;
  }

  &::after {
    right: -1px;
    transform: rotateZ(180deg);
  }
`

const horizontalScrollTrackStyle = css`
  position: relative;
  display: flex;
  gap: 1rem;
  overflow: scroll;

  --ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`
